import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode'; // Import jwt-decode

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token')); // Add state for token

  // Helper: clear auth state and redirect to login
  const forceLogout = (reason) => {
    console.warn('Force logout:', reason);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('adminToken');
    delete axios.defaults.headers.common['Authorization'];
    setCurrentUser(null);
    setToken(null);
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    // Check if there's a user in localStorage
    const userString = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    
    if (userString && storedToken) {
      try {
        // Check if token is expired
        const decoded = jwtDecode(storedToken);
        const now = Date.now() / 1000;
        if (decoded.exp && decoded.exp < now) {
          forceLogout('Token expired on page load');
          setLoading(false);
          return;
        }

        setCurrentUser(JSON.parse(userString));
        setToken(storedToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        console.log('Auth header set from localStorage');
      } catch (e) {
        console.error('Error parsing user/token from localStorage', e);
        forceLogout('Invalid token or user data');
      }
    }
    
    setLoading(false);
  }, []);

  // Global axios interceptor: catch 401/403 from any axios call and force logout
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          forceLogout('API returned ' + error.response.status);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const login = async (username, password) => {
    setError(null);
    console.log('Login attempt in AuthContext for:', username);
    try {
      console.log('Making API request to /api/auth/login');
      const response = await axios.post('/api/auth/login', { username, password });
      console.log('Login API response:', response.data);
      const { token: newToken, user } = response.data; // Rename incoming token
      
      // Store token and user in localStorage
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(user));
      console.log('User data stored in localStorage');
      
      // Set up axios to send the token with all requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      console.log('Authorization header set in axios');
      
      setCurrentUser(user);
      setToken(newToken); // Update token state
      console.log('User set in state:', user);
      return true;
    } catch (err) {
      console.error('Login error in AuthContext:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.message || 'Login failed');
      return false;
    }
  };

  const logout = () => {
    // Remove token and user from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Remove token from axios headers
    delete axios.defaults.headers.common['Authorization'];
    
    setCurrentUser(null);
    setToken(null); // Clear token state
  };

  const updateProfile = async (userData) => {
    setError(null);
    try {
      const response = await axios.put('/api/auth/profile', userData);
      const updatedUser = {
        ...currentUser,
        ...response.data
      };
      
      // Update stored user
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setCurrentUser(updatedUser);
      
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
      return false;
    }
  };

  const isFullAdmin = () => {
    return currentUser?.role === 'full_admin';
  };

  const isRegularAdmin = () => {
    return currentUser?.role === 'regular_admin';
  };

  const isRedaktion = () => {
    return currentUser?.role === 'redaktion';
  };

  const isOrgAdmin = () => {
    return currentUser?.role === 'org_admin';
  };

  // Returns the list of organization IDs for org_admin users
  const getOrgIds = () => {
    return currentUser?.organization_ids || [];
  };

  // Define isAuthenticated based on currentUser
  const isAuthenticated = !!currentUser;

  const value = {
    currentUser,
    token, // Expose token
    loading,
    error,
    isAuthenticated,
    login,
    logout,
    updateProfile,
    isFullAdmin,
    isRegularAdmin,
    isRedaktion,
    isOrgAdmin,
    getOrgIds
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 